import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Flex, Grid, LoadingIndicator } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { withClientContext } from "~/apollo/client";
import {
  useGetArenaQuery,
  useGetCurrentUserQuery,
  useGetEntityTransferSettingsQuery,
} from "~/graphql/mainHooks";
import { toGlobalId } from "~/utils/graphql-helpers";
import { isTransferSyncUser } from "~/utils/userRoles";
import { ArenaParams } from "../../../Entities";
import { NO_TRANSFER_BATCH, NO_TRANSFER_SUBTITLE } from "../constants";
import EmptyState from "../Empty";
import UnsyncedBanner from "./components/Banner";
import EntitySettingsNotRestrictModal from "./components/EntitySettingsNotRestrictModal";
import { BatchRowStatus, Row, colDefs, getRowData, gridOptions } from "./utils";

type Props = {
  rowData: Row[];
  arenaId: number;
  selectedEntityId: number;
  onSync: () => void;
};

const UnsyncedTransferGlBatchTable = (props: Props) => {
  const enableResyncTransfer = useFeatureFlag("resyncTransfer", false);
  const { id } = useParams<ArenaParams>();
  const [entitySettingsNotRestrict, setEntitySettingsNotRestrict] = useState(false);
  const { data: transferSettingsData, loading: transferSettingsLoading } =
    useGetEntityTransferSettingsQuery({
      variables: {
        entityId: toGlobalId("AccountObjectType", props.selectedEntityId || -1),
      },
      context: withClientContext("staff", {
        arenaId: props.arenaId,
      }),
      fetchPolicy: "no-cache",
    });

  const { data: userData, loading: userLoading } = useGetCurrentUserQuery({
    context: withClientContext("staff"),
  });
  // Todo: consider introducing a global state management for this like Redux
  const { data: arenaData, loading: arenaLoading } = useGetArenaQuery({
    context: withClientContext("staff"),
    variables: { id: toGlobalId("ArenaObjectType", id || "") },
  });
  const entityTransferEnabled =
    transferSettingsData?.investmentEntity?.transferAccountingEnabled || false;

  const arena = arenaData?.arena;
  const roles: string[] =
    userData?.currentUser?.roles?.edges
      .map((edge) => edge.node.name)
      .filter((name): name is string => !!name) || [];
  const allowSync = isTransferSyncUser(roles);

  if (!enableResyncTransfer && (!arena || props.rowData.length === 0)) {
    return <EmptyState text={NO_TRANSFER_BATCH} subtitle={NO_TRANSFER_SUBTITLE} />;
  }
  const firstHasError =
    props.rowData.length > 0 && props.rowData[0].status === BatchRowStatus.NOT_READY;
  const anyProcessing = props.rowData.some((row) => row.status === BatchRowStatus.PROCESSING);

  if (userLoading || arenaLoading || transferSettingsLoading) {
    return (
      <Card>
        <LoadingIndicator variant="primary"></LoadingIndicator>
      </Card>
    );
  }

  return (
    <>
      {entitySettingsNotRestrict && arena && (
        <EntitySettingsNotRestrictModal
          arenaName={arena.name}
          arenaSubdomain={arena.secondLevelDomain}
          entityId={props.selectedEntityId}
          onClose={() => setEntitySettingsNotRestrict(false)}
          showModal={entitySettingsNotRestrict}
        />
      )}
      <Card style={{ border: "none" }}>
        {arena && props.rowData.length > 0 ? (
          <>
            {(entityTransferEnabled || firstHasError) && (
              <UnsyncedBanner
                arenaName={arena.name}
                arenaSubdomain={arena.secondLevelDomain}
                entityId={props.selectedEntityId}
                firstTransferDate={props.rowData[0].batchDate}
                positionId={props.rowData[0].positionIds ? props.rowData[0].positionIds[0] : -1}
                entityTransferEnabled={entityTransferEnabled}
              />
            )}
            <Grid
              gridOptions={gridOptions(
                arena.name,
                arena.secondLevelDomain,
                props.selectedEntityId,
                entityTransferEnabled,
              )}
              rowData={getRowData(props.rowData, entityTransferEnabled)}
              columnDefs={colDefs}
            ></Grid>
          </>
        ) : (
          <div style={{ padding: "10px", border: "1px solid #E0E0E0", borderRadius: "8px" }}>
            <EmptyState text={NO_TRANSFER_BATCH} subtitle={NO_TRANSFER_SUBTITLE} />
          </div>
        )}

        <Flex
          justifyContent="flex-end"
          style={{
            marginTop: "24px",
            display: firstHasError || anyProcessing || !allowSync ? "none" : "flex",
          }}
        >
          <Button
            variant="primary"
            onClick={() => {
              if (entityTransferEnabled) {
                setEntitySettingsNotRestrict(true);
              } else {
                props.onSync();
              }
            }}
          >
            Sync transfers
          </Button>
        </Flex>
      </Card>
    </>
  );
};

export default UnsyncedTransferGlBatchTable;
